import dayjs from "dayjs";
import {
  getLocalStorageValue,
  setLocalStorageValue,
} from "../additional/functions";

const VALID_DATE_FORMATS = [
  "YYYY-MM-DD",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DD HH:mm:ss",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "MM-DD-YYYY",
  "DD-MM-YYYY",
];

const isValidDateString = (value) => {
  if (typeof value !== "string" || !value.trim()) return false;

  if (value.length < 8) return false;
  if (!/\d/.test(value)) return false;
  if (/^[a-zA-Z\s]+$/.test(value)) return false;

  const isValidFormat = VALID_DATE_FORMATS.some((format) => {
    const parsed = dayjs(value, format, true); // strict mode
    return parsed.isValid() && parsed.format(format) === value;
  });

  if (isValidFormat) return true;

  // ISO format fallback
  const isoDate = dayjs(value);
  return isoDate.isValid() && /^\d{4}-\d{2}-\d{2}/.test(value);
};

const STORAGE_PREFIX = "tableSettings_"; // Tables prefix

const serializeFilters = (filters) =>
  filters?.map((item) => {
    if (Array.isArray(item.value)) {
      return {
        ...item,
        value: item.value.map((value) =>
          value
            ? typeof value === "string"
              ? value
              : value.toISOString?.() || String(value)
            : null
        ),
      };
    }
    if (
      item.value &&
      typeof item.value === "object" &&
      item.value.start &&
      item.value.end
    ) {
      return {
        ...item,
        value: {
          start: item.value.start
            ? typeof item.value.start === "string"
              ? item.value.start
              : item.value.start.toISOString?.() || String(item.value.start)
            : null,
          end: item.value.end
            ? typeof item.value.end === "string"
              ? item.value.end
              : item.value.end.toISOString?.() || String(item.value.end)
            : null,
        },
      };
    }
    return item;
  });

const deserializeFilters = (filters) =>
  filters?.map((item) => {
    if (Array.isArray(item.value)) {
      return {
        ...item,
        value: item.value.map((value) => (value ? dayjs(value) : null)),
      };
    }
    if (
      item.value &&
      typeof item.value === "object" &&
      item.value.start &&
      item.value.end
    ) {
      return {
        ...item,
        value: {
          start: item.value.start ? dayjs(item.value.start) : null,
          end: item.value.end ? dayjs(item.value.end) : null,
        },
      };
    }
    if (typeof item.value === "string" && isValidDateString(item.value)) {
      return {
        ...item,
        value: dayjs(item.value),
      };
    }
    return item;
  });

export const loadTableState = (tableId) => {
  try {
    const raw = getLocalStorageValue(STORAGE_PREFIX + tableId);
    if (!raw) return undefined;
    const state = JSON.parse(raw);
    if (!Array.isArray(state.columnFilters)) state.columnFilters = [];
    else state.columnFilters = deserializeFilters(state.columnFilters);
    return state;
  } catch (err) {
    console.error(`Failed to load state for ${tableId}`, err);
    return undefined;
  }
};

export const saveTableState = (tableId, state) => {
  try {
    const {
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      density,
      columnFilters,
    } = state;
    const serializedState = JSON.stringify({
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      density,
      columnFilters: serializeFilters(columnFilters),
    });
    setLocalStorageValue(STORAGE_PREFIX + tableId, serializedState);
  } catch (err) {
    console.error(`Failed to save state for ${tableId}`, err);
  }
};
